import React, { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { getAuth0LoginAuthorizationParams, getAuth0OpenUrlOption, getAuth0SignupAuthorizationParams } from '../helpers/auth0Capacitor';
import { logoutWithPushCleanup } from '../helpers/pushDeviceAuth';
import { useConfigInfoService } from '../helpers/ConfigInfo';
import { AppRuntime, AppRuntimeState, getAppRuntime } from './AppRuntime';

const AppRuntimeContext = createContext<AppRuntime | null>(null);

type RuntimeAuthCommandOptions = {
  returnTo?: string;
  redirectUri?: string;
};

type RuntimeSignupCommandOptions = RuntimeAuthCommandOptions & {
  loginHint?: string;
};

const AppRuntimeBridge: React.FC = () => {
  const runtime = useAppRuntime();
  const location = useLocation();
  const configInfoService = useConfigInfoService();
  const {
    error,
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const authService = useMemo(
    () => ({
      requestLogin: async (options: RuntimeAuthCommandOptions = {}) =>
        loginWithRedirect({
          authorizationParams: {
            ...getAuth0LoginAuthorizationParams(),
          },
          appState: {
            returnTo: options.returnTo ?? window.location.href,
            redirectUri: options.redirectUri ?? `${window.location.origin}${window.location.pathname}`,
          },
          ...getAuth0OpenUrlOption(),
        }),
      requestSignup: async (options: RuntimeSignupCommandOptions = {}) =>
        loginWithRedirect({
          authorizationParams: {
            screen_hint: 'signup',
            ...(options.loginHint ? { login_hint: options.loginHint } : {}),
            ...getAuth0SignupAuthorizationParams(),
          },
          appState: {
            returnTo: options.returnTo ?? `${window.location.origin}/dashboard`,
            redirectUri: options.redirectUri ?? `${window.location.origin}/dashboard`,
          },
          ...getAuth0OpenUrlOption(),
        }),
      requestLogout: async (returnToPath = '') =>
        logoutWithPushCleanup({
          configInfoService,
          getAccessTokenSilently,
          getIdTokenClaims,
          logout,
          returnToPath,
        }),
      getAccessTokenSilently: () => getAccessTokenSilently(),
      getIdTokenClaims,
    }),
    [
      configInfoService,
      getAccessTokenSilently,
      getIdTokenClaims,
      loginWithRedirect,
      logout,
    ]
  );

  useEffect(() => {
    runtime.configureServices({
      auth: authService,
      configInfo: configInfoService,
    });
  }, [authService, configInfoService, runtime]);

  useEffect(() => {
    void runtime.dispatchCommand({ type: 'BOOTSTRAP_APP' });
  }, [runtime]);

  useEffect(() => {
    void runtime.dispatchCommand({ type: 'OBSERVE_ROUTE', route: location.pathname });
  }, [location.pathname, runtime]);

  useEffect(() => {
    void runtime.dispatchCommand({
      type: 'OBSERVE_AUTH_STATE',
      isAuthenticated,
      isLoading,
      errorMessage: error?.message ?? null,
    });
  }, [error?.message, isAuthenticated, isLoading, runtime]);

  return null;
};

export const AppRuntimeProvider: React.FC<{
  children: ReactNode;
  runtime?: AppRuntime;
  includeBridge?: boolean;
}> = ({ children, runtime: providedRuntime, includeBridge = true }) => {
  const runtime = useMemo(() => providedRuntime ?? getAppRuntime(), [providedRuntime]);

  return (
    <AppRuntimeContext.Provider value={runtime}>
      {includeBridge && <AppRuntimeBridge />}
      {children}
    </AppRuntimeContext.Provider>
  );
};

export const useAppRuntime = () => {
  const context = useContext(AppRuntimeContext);

  if (!context) {
    throw new Error('useAppRuntime must be used within an AppRuntimeProvider');
  }

  return context;
};

export const useAppRuntimeState = (): AppRuntimeState => {
  const runtime = useAppRuntime();

  return useSyncExternalStore(runtime.subscribe, runtime.getState, runtime.getState);
};

export const useAppApiService = () => {
  const runtime = useAppRuntime();

  useAppRuntimeState();
  return runtime.getApiService();
};

export const useAppCommands = () => {
  const runtime = useAppRuntime();

  return useMemo(
    () => ({
      requestLogin: (options?: RuntimeAuthCommandOptions) =>
        runtime.dispatchCommand({ type: 'REQUEST_LOGIN', ...options }),
      requestSignup: (options?: RuntimeSignupCommandOptions) =>
        runtime.dispatchCommand({ type: 'REQUEST_SIGNUP', ...options }),
      requestLogout: (returnToPath?: string) =>
        runtime.dispatchCommand({ type: 'REQUEST_LOGOUT', returnToPath }),
      selectDashboardHome: () => runtime.dispatchCommand({ type: 'SELECT_DASHBOARD_HOME' }),
      selectMakeReferral: () => runtime.dispatchCommand({ type: 'SELECT_MAKE_REFERRAL' }),
      selectMakeRecommendation: () =>
        runtime.dispatchCommand({ type: 'SELECT_MAKE_RECOMMENDATION' }),
      selectForceUser: () => runtime.dispatchCommand({ type: 'SELECT_FORCE_USER' }),
      submitTermsAcceptance: () => runtime.dispatchCommand({ type: 'SUBMIT_TERMS_ACCEPTANCE' }),
    }),
    [runtime]
  );
};
